/**
 * OnboardingListPage — super-admin sales-pipeline view of every active
 * onboarding session. Shows completion %, next step, and a deep-link
 * into the wizard for each org.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../lib/authedFetch';
import { toast } from 'sonner';
import { Sparkles, Loader2, ArrowRight, ClipboardCheck, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/layout/PageShell';
import PageHero from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API = process.env.REACT_APP_BACKEND_URL;

function statusChip(s) {
  if (s.status === 'complete') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s.completion_pct >= 50) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

export default function OnboardingListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const pageTheme = React.useMemo(() => getLightSectionTheme('Indigo'), []);

  useEffect(() => {
    (async () => {
      try {
        const r = await authedFetch(`${API}/api/onboarding`);
        if (!r.ok) throw new Error(await r.text());
        const d = await r.json();
        setRows(d.sessions || []);
      } catch (e) {
        toast.error(`Failed to load onboarding list: ${e.message || e}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadChecklist = async () => {
    const r = await authedFetch(`${API}/api/onboarding/data-readiness-checklist`);
    const md = await r.text();
    const blob = new Blob([md], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'Seertech_Data_Readiness_Checklist.md';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <PageShell
      theme={pageTheme}
      hero={(
        <PageHero
          icon={Sparkles}
          theme={pageTheme}
          title="Onboarding Pipeline"
          description="Every org currently in the activation wizard. Each row deep-links into the resume point — share with the customer or finish it yourself."
          stats={[
            { label: 'Total', value: String(rows.length) },
            { label: 'In progress', value: String(rows.filter((r) => r.status === 'in_progress').length) },
            { label: 'Activated', value: String(rows.filter((r) => r.status === 'complete').length) },
            { label: 'Avg completion', value: rows.length ? `${Math.round(rows.reduce((acc, r) => acc + (r.completion_pct || 0), 0) / rows.length)}%` : '0%' },
          ]}
          testId="onboarding-list-hero"
        />
      )}
      data-testid="onboarding-list-page"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 py-8 space-y-4">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={downloadChecklist} data-testid="onboarding-list-download-checklist">
            <FileDown className="h-3.5 w-3.5 mr-2" /> Data Readiness Checklist
          </Button>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="onboarding-list-empty">
            <ClipboardCheck className="mx-auto h-8 w-8 text-indigo-400 mb-2" />
            <p className="text-sm font-medium text-slate-700">No active onboarding sessions yet</p>
            <p className="mt-1 text-xs text-slate-500">Sessions start automatically when you create a new org from the dashboard.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="onboarding-list-grid">
            {rows.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-4 hover:border-indigo-200 transition-colors" data-testid={`onboarding-list-row-${r.org_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.org_id}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.id}</div>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusChip(r)}`}>
                    {r.status} · {r.completion_pct}%
                  </span>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{ width: `${r.completion_pct}%` }}
                  />
                </div>
                <div className="mt-3 text-[11px] text-slate-600">
                  Next step: <span className="font-semibold">{r.next_step || '—'}</span>
                  {(r.missing || []).length > 0 && (
                    <span className="text-slate-400"> · {r.missing.length} required item{r.missing.length === 1 ? '' : 's'} remaining</span>
                  )}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={() => navigate(`/admin/onboarding/${r.org_id}`)} data-testid={`onboarding-list-resume-${r.org_id}`}>
                    Continue <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
